import axios from "axios";
import React from "react";
import { clubOf, ClubLogo } from "../../components/auth/AuthShell";
import { classNames } from "../../helpers";
import { useTenant } from "../../provider/TenantProvider";
import { getUrlForBookings } from "../../utils/confighelpers";
import { defaultDomainList } from "../../utils/constants";

/**
 * Free trial class booking for newcomers (no account, no password). Backend: functions/trial/trialBooking.js
 *   /trial                     open schedule        (TrialSchedulePage)
 *   /trial/book/:sessionId     details + SMS code   (TrialBookPage)
 *   /trial/manage?c=<code>     change or cancel     (TrialManagePage, link in the SMS / email)
 * Built for phones first: most people open the club's "Book a free trial" link on their phone.
 */

export const TZ = "Europe/Amsterdam";
const API = () => getUrlForBookings();

export const apiMessage = (res, fallback) =>
  (res?.data && typeof res.data.message === "string" && res.data.message) || fallback;

/** POST / GET to a trial endpoint. Returns { ok, data, message }. */
export async function trialApi(method, path, body) {
  try {
    const res = method === "get"
      ? await axios.get(`${API()}${path}`, { params: body })
      : await axios.post(`${API()}${path}`, body);
    if (res.status === 200 && res.data?.statusCode === 200) return { ok: true, data: res.data.data, message: res.data.message };
    return { ok: false, message: apiMessage(res, "Something went wrong. Please try again.") };
  } catch (e) {
    return { ok: false, message: apiMessage(e?.response, "Couldn't reach the server. Please check your connection and try again.") };
  }
}

/** The club this page is for: the club's own address (subdomain), or ?club= on a FitClub address. */
export function useTrialClub() {
  const { tenant, tenantData } = useTenant();
  const fromQuery = React.useMemo(() => (new URLSearchParams(window.location.search).get("club") || "").trim().toLowerCase(), []);
  const isClubAddress = defaultDomainList.indexOf(tenant) < 0;
  const link = isClubAddress ? tenant : fromQuery;
  const details = isClubAddress && tenantData?.business_details ? tenantData.business_details : null;
  return { link, details };
}

/* ---------------------------------------------------------------- dates (club time) */

const partsOf = (date) => {
  const p = {};
  new Intl.DateTimeFormat("en-GB", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" })
    .formatToParts(new Date(date))
    .forEach((x) => { p[x.type] = x.value; });
  return p;
};
/** "2026-10-27" in club time */
export const dayKey = (date) => {
  const p = partsOf(date);
  return `${p.year}-${p.month}-${p.day}`;
};
/** Noon on that day, so adding days never trips over the clock change. */
export const keyToDate = (key) => new Date(`${key}T12:00:00Z`);
export const addDays = (key, n) => {
  const d = keyToDate(key);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const fmt = (date, opts) => new Intl.DateTimeFormat("en-GB", { timeZone: TZ, ...opts }).format(new Date(date));
export const timeText = (date) => fmt(date, { hour: "2-digit", minute: "2-digit", hour12: false });
export const longDay = (date) => fmt(date, { weekday: "long", day: "numeric", month: "long" });
export const shortDay = (date) => fmt(date, { weekday: "short", day: "numeric", month: "short" });
const keyLabel = (key, opts) => new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", ...opts }).format(keyToDate(key));
export const durationText = (min) => {
  const m = Number(min) || 0;
  if (!m) return "";
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h ? (r ? `${h} h ${r} min` : `${h} h`) : `${r} min`;
};
export const placeText = (loc) =>
  [loc?.addressName, [loc?.address, loc?.houseNumber].filter(Boolean).join(" "), loc?.city].filter(Boolean).join(", ");
export const mapsUrl = (loc) => {
  const q = [[loc?.address, loc?.houseNumber].filter(Boolean).join(" "), loc?.zip, loc?.city].filter(Boolean).join(", ") || loc?.addressName;
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : "";
};

/** Calendar file for the booked class (works on iPhone and Android). */
export function downloadIcs(session, clubName) {
  const z = (d) => new Date(d).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const end = session.classEndDate || new Date(new Date(session.classDate).getTime() + (session.duration || 60) * 60000);
  const esc = (s) => String(s || "").replace(/([,;\\])/g, "\\$1").replace(/\n/g, "\\n");
  const ics = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//FitClub//Trial//EN", "BEGIN:VEVENT",
    `UID:trial-${session._id}@fitclub`, `DTSTAMP:${z(new Date())}`, `DTSTART:${z(session.classDate)}`, `DTEND:${z(end)}`,
    `SUMMARY:${esc(`${session.className} (free trial) - ${clubName}`)}`,
    `LOCATION:${esc(placeText(session.location))}`,
    "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "trial-class.ics";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/* ---------------------------------------------------------------- icons */

const I = ({ d, className = "h-4 w-4", strokeWidth = 2 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    {d}
  </svg>
);
export const Icons = {
  pin: (c) => <I className={c} d={<><path d="M12 21s-7-6.2-7-12a7 7 0 0 1 14 0c0 5.8-7 12-7 12Z" /><circle cx="12" cy="9" r="2.5" /></>} />,
  user: (c) => <I className={c} d={<><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>} />,
  clock: (c) => <I className={c} d={<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>} />,
  back: (c) => <I className={c} d={<path d="m15 18-6-6 6-6" />} />,
  check: (c) => <I className={c} strokeWidth={2.6} d={<path d="m5 12 5 5 9-10" />} />,
  cal: (c) => <I className={c} d={<><rect x="3" y="5" width="18" height="16" rx="3" /><path d="M3 10h18M8 3v4M16 3v4" /></>} />,
  phone: (c) => <I className={c} d={<><rect x="6" y="2" width="12" height="20" rx="3" /><path d="M11 18h2" /></>} />,
  mail: (c) => <I className={c} d={<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>} />,
  chevron: (c) => <I className={c} d={<path d="m9 6 6 6-6 6" />} />,
};

/* ---------------------------------------------------------------- layout */

/** Club header + a single column, comfortable on a phone, centred on a computer. */
export function TrialLayout({ details, apiClub, title, subtitle, children, compact = false }) {
  const c = clubOf(details);
  const name = c.name || apiClub?.name || "";
  const logoClub = details || (apiClub ? { business_detail: { businessName: apiClub.name, logo: apiClub.logo } } : null);
  return (
    <div className="min-h-screen bg-[#F7F4F8]">
      <header className="relative overflow-hidden bg-brown-coffee text-white">
        {c.cover && !compact && <img src={c.cover} alt="" className="absolute inset-0 h-full w-full object-cover opacity-35" />}
        <div className="absolute inset-0 bg-gradient-to-b from-[#481B3A]/40 to-[#481B3A]" aria-hidden="true" />
        <div className={classNames("relative mx-auto max-w-xl px-4", compact ? "py-4" : "pb-6 pt-5")}>
          <div className="flex items-center gap-3">
            {logoClub ? <ClubLogo club={logoClub} className="h-11 w-11 rounded-2xl text-base" /> : <span className="h-11 w-11 rounded-2xl bg-white/20" />}
            <div className="min-w-0">
              <div className="truncate text-[17px] font-black leading-tight">{name || " "}</div>
              {c.city && <div className="truncate text-[13px] font-semibold text-white/80">{c.city}</div>}
            </div>
          </div>
          {title && <h1 className="mt-5 text-[28px] font-black leading-[1.1] tracking-tight">{title}</h1>}
          {subtitle && <div className="mt-2 text-[15px] font-semibold leading-snug text-white/85">{subtitle}</div>}
        </div>
      </header>
      <main className="mx-auto max-w-xl px-4 pb-16">{children}</main>
    </div>
  );
}

export function Card({ className = "", children }) {
  return <div className={classNames("rounded-3xl border border-[#ECE4EF] bg-white", className)}>{children}</div>;
}

export function PrimaryButton({ children, busy = false, disabled = false, type = "button", onClick, tone = "plum", className = "" }) {
  const tones = {
    plum: "bg-brown-coffee text-white hover:bg-light-brown-coffee",
    coral: "bg-portland-orange text-white hover:bg-light-portland-orange",
    soft: "bg-[#F3ECF5] text-brown-coffee hover:bg-[#EADFEE]",
    danger: "bg-white text-[#B42335] border border-[#F2C9CF] hover:bg-[#FDF0F1]",
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={busy || disabled}
      className={classNames("flex h-[54px] w-full items-center justify-center gap-2 rounded-2xl text-[17px] font-extrabold transition-colors disabled:opacity-60", tones[tone], className)}
    >
      {busy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />}
      {children}
    </button>
  );
}

export function Alert({ children, tone = "error" }) {
  if (!children) return null;
  const t = tone === "error" ? "border-[#F5C2C7] bg-[#FDF0F1] text-[#8A1C2B]" : "border-[#CFE8D8] bg-[#EEF8F2] text-[#1F6B3A]";
  return <div role={tone === "error" ? "alert" : "status"} className={classNames("rounded-2xl border px-4 py-3 text-[15px] font-semibold", t)}>{children}</div>;
}

/** Photo, or a coloured initial. */
export function ClassThumb({ session, className = "h-14 w-14" }) {
  return session.classPhoto ? (
    <img src={session.classPhoto} alt="" className={classNames("shrink-0 rounded-2xl object-cover", className)} loading="lazy" />
  ) : (
    <span className={classNames("flex shrink-0 items-center justify-center rounded-2xl bg-[#F3ECF5] text-xl font-black text-brown-coffee", className)}>
      {(session.className || "?").slice(0, 1).toUpperCase()}
    </span>
  );
}

/** The chosen class, as on the confirmation: name, day + time, coach, place. */
export function SessionSummary({ session, className = "" }) {
  if (!session) return null;
  return (
    <Card className={classNames("p-4", className)}>
      <div className="flex items-center gap-3.5">
        <ClassThumb session={session} />
        <div className="min-w-0">
          <div className="truncate text-[17px] font-black text-gray-900">{session.className}</div>
          <div className="text-[15px] font-bold text-brown-coffee">
            {longDay(session.classDate)} · {timeText(session.classDate)}
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-col gap-1.5 text-[14px] text-gray-600">
        {durationText(session.duration) && <div className="flex items-center gap-2">{Icons.clock("h-4 w-4 shrink-0 text-gray-400")}{durationText(session.duration)}</div>}
        {session.instructorName && <div className="flex items-center gap-2">{Icons.user("h-4 w-4 shrink-0 text-gray-400")}with {session.instructorName}</div>}
        {placeText(session.location) && (
          <div className="flex items-start gap-2">{Icons.pin("mt-0.5 h-4 w-4 shrink-0 text-gray-400")}<span>{placeText(session.location)}</span></div>
        )}
      </div>
    </Card>
  );
}

/** How to reach the club (for "contact the club" messages). */
export function ClubContact({ club }) {
  if (!club?.workNumber && !club?.workEmail) return null;
  return (
    <div className="flex flex-col gap-2">
      {club.workNumber && (
        <a href={`tel:${club.workNumber.replace(/\s+/g, "")}`} className="flex items-center gap-2 text-[15px] font-bold text-brown-coffee">
          {Icons.phone("h-4 w-4")} {club.workNumber}
        </a>
      )}
      {club.workEmail && (
        <a href={`mailto:${club.workEmail}`} className="flex items-center gap-2 break-all text-[15px] font-bold text-brown-coffee">
          {Icons.mail("h-4 w-4")} {club.workEmail}
        </a>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- schedule picker */

const WINDOW = 14;

/**
 * One class on the schedule. Phone first: time and length on top, the class name in full, coach and place on their
 * own lines, then spots left + the button on a row of their own. The class photo sits top right.
 */
function SessionCard({ session, onPick, actionLabel, current }) {
  const few = !session.isFull && session.spotsLeft > 0 && session.spotsLeft <= 3;
  const disabled = session.isFull || current;
  const where = session.location?.addressName || session.location?.city || "";
  const end = session.classEndDate ? timeText(session.classEndDate) : "";
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onPick(session)}
      className={classNames(
        "flex w-full flex-col rounded-3xl border bg-white p-5 text-left transition-shadow",
        disabled ? "border-[#EEE8F0]" : "border-[#ECE4EF] active:scale-[0.99] hover:shadow-md"
      )}
    >
      <div className="flex w-full items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className={classNames("flex items-center gap-2 text-[15px] font-extrabold tabular-nums", session.isFull ? "text-gray-400" : "text-brown-coffee")}>
            {Icons.clock("h-4 w-4 shrink-0")}
            <span>{timeText(session.classDate)}{end ? ` – ${end}` : ""}</span>
            {durationText(session.duration) && <span className="font-semibold text-gray-500">· {durationText(session.duration)}</span>}
          </div>
          <div className={classNames("mt-2 text-[20px] font-black leading-tight tracking-tight", session.isFull ? "text-gray-500" : "text-gray-900")}>
            {session.className}
          </div>
          <div className="mt-2 flex flex-col gap-1 text-[14px] text-gray-600">
            {session.instructorName && (
              <div className="flex items-center gap-2">{Icons.user("h-4 w-4 shrink-0 text-gray-400")}<span className="truncate">{session.instructorName}</span></div>
            )}
            {where && (
              <div className="flex items-center gap-2">{Icons.pin("h-4 w-4 shrink-0 text-gray-400")}<span className="truncate">{where}</span></div>
            )}
          </div>
        </div>
        <ClassThumb session={session} className={classNames("h-[72px] w-[72px] sm:h-20 sm:w-20", disabled && !current ? "opacity-50" : "")} />
      </div>

      <div className="mt-4 flex w-full items-center justify-between gap-3 border-t border-[#F1ECF3] pt-4">
        <span className={classNames("text-[14px] font-bold", current ? "text-[#1F6B3A]" : session.isFull ? "text-gray-500" : few ? "text-portland-orange" : "text-gray-600")}>
          {current ? "Your class" : session.isFull ? "Fully booked" : session.spotsLeft === 1 ? "1 spot left" : `${session.spotsLeft} spots left`}
        </span>
        {!disabled && (
          <span className="flex h-11 shrink-0 items-center rounded-full bg-portland-orange px-6 text-[15px] font-extrabold text-white">{actionLabel}</span>
        )}
      </div>
    </button>
  );
}

function DayChip({ dayKey: k, selected, today, count, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={classNames(
        "flex h-[68px] w-[54px] shrink-0 snap-start flex-col items-center justify-center rounded-2xl border transition-colors",
        selected ? "border-brown-coffee bg-brown-coffee text-white" : "border-[#ECE4EF] bg-white text-gray-900",
        !selected && !count ? "opacity-50" : ""
      )}
    >
      <span className={classNames("text-[11px] font-extrabold uppercase tracking-wide", selected ? "text-white/80" : "text-gray-500")}>
        {today ? "Today" : keyLabel(k, { weekday: "short" })}
      </span>
      <span className="text-[20px] font-black leading-tight">{keyLabel(k, { day: "numeric" })}</span>
      <span className={classNames("mt-0.5 h-1.5 w-1.5 rounded-full", count ? (selected ? "bg-white" : "bg-portland-orange") : "bg-transparent")} />
    </button>
  );
}

/**
 * Day strip + the classes of the chosen day. Loads 14 days at a time from trialSchedule.
 * onPick(session) when a class is tapped. currentId: the guest's own class (shown, not tappable).
 */
export function SessionPicker({ clubLink, onPick, onLoaded, actionLabel = "Try free", currentId = "", startDay = "" }) {
  const todayKey = React.useMemo(() => dayKey(new Date()), []);
  const [sessions, setSessions] = React.useState([]);
  const [loadedTo, setLoadedTo] = React.useState(""); // exclusive
  const [lastDay, setLastDay] = React.useState("");
  const [selected, setSelected] = React.useState("");
  const [classFilter, setClassFilter] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const stripRef = React.useRef(null);

  const load = React.useCallback(async (from) => {
    setLoading(true);
    const r = await trialApi("get", "trialSchedule", { club: clubLink, from, days: WINDOW });
    setLoading(false);
    if (!r.ok) {
      setError(r.message);
      return null;
    }
    setError("");
    setSessions((prev) => {
      const seen = new Set(prev.map((s) => s._id));
      return [...prev, ...r.data.sessions.filter((s) => !seen.has(s._id))];
    });
    setLoadedTo(r.data.to || addDays(from, WINDOW));
    setLastDay(r.data.lastDay || "");
    onLoaded && onLoaded(r.data);
    return r.data;
  }, [clubLink, onLoaded]);

  React.useEffect(() => {
    if (!clubLink) return;
    (async () => {
      const d = await load(todayKey);
      // start on the first day with a class
      // start on startDay (e.g. the guest's current class), else the first day with a class
      if (startDay && startDay >= todayKey && (!d?.to || startDay < d.to)) {
        setSelected(startDay);
        return;
      }
      const first = d?.sessions?.find((s) => !s.isFull) || d?.sessions?.[0];
      setSelected(first ? dayKey(first.classDate) : todayKey);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubLink]);

  // keep the chosen day in view in the strip
  React.useEffect(() => {
    const el = stripRef.current?.querySelector(`[data-day="${selected}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [selected]);

  const classes = React.useMemo(() => {
    const m = new Map();
    sessions.forEach((s) => { if (!m.has(s.classId)) m.set(s.classId, s.className); });
    return [...m.entries()];
  }, [sessions]);
  const visible = sessions.filter((s) => !classFilter || s.classId === classFilter);
  const byDay = React.useMemo(() => {
    const m = {};
    visible.forEach((s) => { const k = dayKey(s.classDate); (m[k] = m[k] || []).push(s); });
    return m;
  }, [visible]);

  const days = [];
  if (loadedTo) for (let k = todayKey; k < loadedTo; k = addDays(k, 1)) days.push(k);
  const canLoadMore = loadedTo && (!lastDay || loadedTo <= lastDay);
  const dayList = byDay[selected] || [];
  const nextDayWithClasses = days.find((k) => k > selected && (byDay[k] || []).length);

  if (error && !sessions.length) {
    return <Alert>{error}</Alert>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="sticky top-0 z-10 -mx-4 bg-[#F7F4F8]/95 px-4 pb-3 pt-3 backdrop-blur">
        <div ref={stripRef} className="flex snap-x scroll-px-1 gap-2 overflow-x-auto px-1 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {!loadedTo && Array.from({ length: 7 }).map((_, i) => <span key={i} className="h-[68px] w-[54px] shrink-0 animate-pulse rounded-2xl bg-[#EDE6F0]" />)}
          {days.map((k) => (
            <div key={k} data-day={k}>
              <DayChip dayKey={k} today={k === todayKey} selected={k === selected} count={(byDay[k] || []).length} onClick={() => setSelected(k)} />
            </div>
          ))}
          {canLoadMore && (
            <button
              type="button"
              onClick={async () => { const d = await load(loadedTo); if (d) setSelected(loadedTo); }}
              disabled={loading}
              className="flex h-[68px] w-[64px] shrink-0 flex-col items-center justify-center rounded-2xl border border-dashed border-[#D9CCE0] bg-white text-[12px] font-extrabold text-brown-coffee"
            >
              {loading ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-brown-coffee border-t-transparent" /> : <>Later{Icons.chevron("h-4 w-4")}</>}
            </button>
          )}
        </div>
        {classes.length > 1 && (
          <div className="mt-2 flex gap-2 overflow-x-auto px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {[["", "All classes"], ...classes].map(([id, name]) => (
              <button
                key={id || "all"}
                type="button"
                onClick={() => setClassFilter(id)}
                aria-pressed={classFilter === id}
                className={classNames(
                  "h-9 shrink-0 whitespace-nowrap rounded-full border px-3.5 text-[14px] font-bold",
                  classFilter === id ? "border-brown-coffee bg-[#F3ECF5] text-brown-coffee" : "border-[#E3DCE6] bg-white text-gray-700"
                )}
              >
                {name}
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && <h2 className="text-[15px] font-extrabold text-gray-900">{keyLabel(selected, { weekday: "long", day: "numeric", month: "long" })}</h2>}

      {loading && !sessions.length ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-[190px] animate-pulse rounded-3xl bg-[#EDE6F0]" />)}
        </div>
      ) : dayList.length ? (
        <div className="flex flex-col gap-3">
          {dayList.map((s) => (
            <SessionCard key={s._id} session={s} onPick={onPick} actionLabel={actionLabel} current={s._id === currentId} />
          ))}
        </div>
      ) : (
        <Card className="flex flex-col items-center gap-3 px-5 py-8 text-center">
          <div className="text-[16px] font-extrabold text-gray-900">No classes on this day</div>
          {nextDayWithClasses ? (
            <button type="button" onClick={() => setSelected(nextDayWithClasses)} className="flex items-center gap-1 text-[15px] font-extrabold text-brown-coffee">
              Next class: {keyLabel(nextDayWithClasses, { weekday: "long", day: "numeric", month: "short" })} {Icons.chevron("h-4 w-4")}
            </button>
          ) : canLoadMore ? (
            <button type="button" onClick={async () => { const d = await load(loadedTo); if (d) setSelected(loadedTo); }} className="text-[15px] font-extrabold text-brown-coffee">
              Show later dates
            </button>
          ) : sessions.length === 0 ? (
            <div className="text-[14px] text-gray-600">There are no trial classes planned right now. Please check again soon.</div>
          ) : null}
        </Card>
      )}
      {error && sessions.length > 0 && <Alert>{error}</Alert>}
    </div>
  );
}
